/**
 * Geocoding utilities using OpenStreetMap Nominatim API
 * Free, no API key required, but has rate limits (1 request per second)
 */

export interface GeocodeResult {
  displayName: string;
  latitude: number;
  longitude: number;
  address: {
    houseNumber?: string;
    road?: string;
    city?: string;
    state?: string;
    postcode?: string;
    country?: string;
  };
}

export interface GeocodeSearchResult {
  displayName: string;
  latitude: number;
  longitude: number;
  placeId: string;
}

/**
 * Search for addresses using Nominatim
 * @param query - Address search query
 * @returns Array of search results
 */
export async function searchAddress(query: string): Promise<GeocodeSearchResult[]> {
  if (!query || query.trim().length < 3) {
    return [];
  }

  try {
    // Add delay to respect rate limits (1 request per second)
    await new Promise((resolve) => setTimeout(resolve, 1000));

    const response = await fetch(
      `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
        query
      )}&limit=5&addressdetails=1`,
      {
        headers: {
          'User-Agent': 'Light-Logix-Control-System', // Required by Nominatim
        },
      }
    );

    if (!response.ok) {
      throw new Error('Geocoding request failed');
    }

    const data = (await response.json()) as Array<{
      display_name: string;
      lat: string;
      lon: string;
      place_id: number | string;
    }>;

    return data.map((item) => ({
      displayName: item.display_name,
      latitude: parseFloat(item.lat),
      longitude: parseFloat(item.lon),
      placeId: String(item.place_id),
    }));
  } catch (error) {
    console.error('Geocoding error:', error);
    return [];
  }
}

/**
 * Reverse geocode coordinates to get address
 * @param latitude - Latitude coordinate
 * @param longitude - Longitude coordinate
 * @returns Address information
 */
export async function reverseGeocode(
  latitude: number,
  longitude: number
): Promise<GeocodeResult | null> {
  try {
    // Add delay to respect rate limits
    await new Promise((resolve) => setTimeout(resolve, 1000));

    const response = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&addressdetails=1`,
      {
        headers: {
          'User-Agent': 'Light-Logix-Control-System',
        },
      }
    );

    if (!response.ok) {
      throw new Error('Reverse geocoding request failed');
    }

    const data = await response.json();

    if (!data || !data.address) {
      return null;
    }

    return {
      displayName: data.display_name,
      latitude: parseFloat(data.lat),
      longitude: parseFloat(data.lon),
      address: {
        houseNumber: data.address.house_number,
        road: data.address.road,
        city: data.address.city || data.address.town || data.address.village,
        state: data.address.state,
        postcode: data.address.postcode,
        country: data.address.country,
      },
    };
  } catch (error) {
    console.error('Reverse geocoding error:', error);
    return null;
  }
}

/**
 * Format address from geocode result
 */
export function formatAddress(result: GeocodeResult): string {
  const parts: string[] = [];

  if (result.address.houseNumber && result.address.road) {
    parts.push(`${result.address.houseNumber} ${result.address.road}`);
  } else if (result.address.road) {
    parts.push(result.address.road);
  }

  if (result.address.city) {
    parts.push(result.address.city);
  }

  if (result.address.state) {
    parts.push(result.address.state);
  }

  if (result.address.postcode) {
    parts.push(result.address.postcode);
  }

  if (result.address.country) {
    parts.push(result.address.country);
  }

  return parts.join(', ') || result.displayName;
}

